import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import * as styles from './index.css'

const ScrollContext = createContext<{
  containerRef: React.RefObject<HTMLDivElement>
  hideIndex: number
  setHideIndex: (index: number) => void
} | null>(null)

const Container = (props: { children: ReactNode }) => {
  const [hideIndex, setHideIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <ScrollContext.Provider value={{ containerRef, hideIndex, setHideIndex }}>
      <div
        ref={containerRef}
        className={styles.container}
        onScroll={() => {}}
      >
        {props.children}
      </div>
    </ScrollContext.Provider>
  )
}

const Interceptor = (props: {
  children: ReactNode
  waitForPrevious?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const context = useContext(ScrollContext)
  const [isVisible, setIsVisible] = useState(true)
  const index = useRef<number>(Math.random())

  if (!context) {
    throw new Error('Interceptor must be used within a Scroll.Container')
  }

  useEffect(() => {
    if (!context.containerRef.current) {
      console.error('There is no context.containerRef.current')
      return
    }

    const handleScroll = () => {
      if (!ref.current) return

      const rect = ref.current.getBoundingClientRect()

      if (context.hideIndex === index.current) {
        if (rect.bottom <= 0) {
          setIsVisible(false)
          context.setHideIndex(context.hideIndex + 1)
        }
      } else if (context.hideIndex > index.current) {
        setIsVisible(false)
      } else {
        setIsVisible(true)
      }
    }

    context.containerRef.current.addEventListener('scroll', handleScroll)

    return () =>
      context.containerRef.current?.removeEventListener?.(
        'scroll',
        handleScroll,
      )
  }, [context, context.hideIndex])

  return (
    <div
      ref={ref}
      className={`${styles.interceptor} ${!isVisible ? styles.hidden : ''}`}
    >
      {props.children}
    </div>
  )
}

const Scrollable = (props: { children: ReactNode }) => {
  return <div className={styles.content}>{props.children}</div>
}

export const ScrollContainer = {
  Container,
  Interceptor,
  Scrollable,
}
